/**
 * `office:spreadsheet` parsing and surgical cell rewriting.
 *
 * ODF stores a grid differently from OOXML in one way that decides the whole
 * shape of this module: **cells have no addresses.** A worksheet part in XLSX
 * writes `<c r="B4">`, so finding B4 is a search. ODF writes cells positionally
 * and compresses runs of identical ones with `table:number-columns-repeated`,
 * so B4 is wherever the running column count lands — and a single element can
 * stand for a thousand cells.
 *
 * Two consequences, both handled here:
 *
 * - **Reading must not expand the padding.** A LibreOffice sheet declares its
 *   rows out to 1 048 576 and its columns out to 16 384, almost all as repeated
 *   empties. Materialising those is the difference between a 40 KB file opening
 *   instantly and the tab dying, so only cells that carry something become
 *   cells (§33).
 * - **Writing must split the run.** Editing one cell inside a repeat group
 *   means replacing that one element with up to three: the columns before, the
 *   edited cell, and the columns after. Rewriting the group in place would
 *   change every cell it covered — a data-loss bug that no amount of care in
 *   the UI could prevent.
 *
 * As in the OOXML handlers, everything outside the edited cell is left byte-for
 * byte alone, so charts, pivot tables, named ranges, conditional formats and
 * the rest survive a save without being understood (§13, §24).
 */
import type { XlsxCell, XlsxSheet } from '../xlsx/handler.js';
import type { CellValue } from '../xlsx/sheet-xml.js';
import { formatRef, parseRef } from '../xlsx/sheet-xml.js';
import { escapeXml } from '../ooxml/xml.js';
import { HandlerError } from '../contract.js';
import { attr, childElements, findElement, intAttr, tokenize } from './scan.js';
import type { ElementSpan } from './scan.js';

/**
 * How many times one repeated element may be materialised.
 *
 * A repeat group holding *content* is the only case this bounds — repeated
 * empties cost nothing because they are never materialised at all. Real files
 * use content repeats for a filled row of headers, which is dozens, not
 * thousands.
 */
const MAX_CONTENT_REPEAT = 512;

export interface OdfSheetParseOptions {
  readonly maxCells: number;
}

export interface ParsedOdfSheets {
  readonly sheets: readonly XlsxSheet[];
  readonly truncated: boolean;
  readonly hasFormulas: boolean;
  /** Sheet names that carry a `table:protected` flag. */
  readonly protectedSheets: readonly string[];
}

/* ------------------------------------------------------------------ *
 * reading
 * ------------------------------------------------------------------ */

/**
 * Parse the whole workbook.
 *
 * `XlsxSheet.part` is used by the model as the key an edit is filed under. ODF
 * keeps every sheet in the same `content.xml`, so the part alone would not tell
 * two sheets apart; the sheet index is appended as a fragment to give each one
 * an identity the shared editing code can use unchanged.
 */
export function parseOdfSheets(
  xml: string, options: OdfSheetParseOptions,
): ParsedOdfSheets {
  const body = findSpreadsheetBody(xml);
  const sheets: XlsxSheet[] = [];
  const protectedSheets: string[] = [];
  let hasFormulas = false;
  let budget = options.maxCells;
  let truncated = false;

  if (!body) return { sheets, truncated: false, hasFormulas, protectedSheets };

  for (const table of childElements(xml, body, ['table:table'])) {
    const name = attr(table.attrs, 'table:name') ?? `Sheet${sheets.length + 1}`;
    if (attr(table.attrs, 'table:protected') === 'true') protectedSheets.push(name);

    const parsed = readTable(xml, table, budget);
    budget -= parsed.cells.length;
    if (parsed.truncated) truncated = true;
    if (parsed.hasFormulas) hasFormulas = true;

    sheets.push({
      name,
      part: `content.xml#${sheets.length}`,
      cells: parsed.cells,
      rowCount: parsed.rowCount,
      colCount: parsed.colCount,
    });

    if (budget <= 0) { truncated = true; break; }
  }

  return { sheets, truncated, hasFormulas, protectedSheets };
}

interface TableRead {
  readonly cells: XlsxCell[];
  readonly rowCount: number;
  readonly colCount: number;
  readonly truncated: boolean;
  readonly hasFormulas: boolean;
}

function readTable(xml: string, table: ElementSpan, budget: number): TableRead {
  const cells: XlsxCell[] = [];
  let rowCount = 0;
  let colCount = 0;
  let truncated = false;
  let hasFormulas = false;
  let row = 0;

  for (const rowSpan of tableRows(xml, table)) {
    const repeat = intAttr(rowSpan.attrs, 'table:number-rows-repeated', 1);
    const parsed = readRow(xml, rowSpan);
    if (parsed.hasFormulas) hasFormulas = true;

    if (parsed.cells.length === 0) {
      // Nothing in the row, whatever its repeat says. This is the branch that
      // makes a padded sheet cheap.
      row += repeat;
      continue;
    }

    const materialise = Math.min(repeat, MAX_CONTENT_REPEAT);
    if (materialise < repeat) truncated = true;

    let full = false;
    for (let r = 0; r < materialise && !full; r++) {
      for (const cell of parsed.cells) {
        if (cells.length >= budget) { truncated = true; full = true; break; }
        cells.push({ ...cell, row: row + r, ref: formatRef(row + r, cell.col) });
        colCount = Math.max(colCount, cell.col + 1);
        rowCount = Math.max(rowCount, row + r + 1);
      }
    }
    if (full) break;
    row += repeat;
  }

  return { cells, rowCount, colCount, truncated, hasFormulas };
}

interface RowRead {
  readonly cells: XlsxCell[];
  readonly hasFormulas: boolean;
}

function readRow(xml: string, rowSpan: ElementSpan): RowRead {
  const cells: XlsxCell[] = [];
  let hasFormulas = false;
  let col = 0;

  for (const cellSpan of childElements(xml, rowSpan, ['table:table-cell', 'table:covered-table-cell'])) {
    const repeat = intAttr(cellSpan.attrs, 'table:number-columns-repeated', 1);
    const value = readCell(xml, cellSpan);

    if (value === null) { col += repeat; continue; }
    if (value.formula !== null) hasFormulas = true;

    for (let i = 0; i < Math.min(repeat, MAX_CONTENT_REPEAT); i++) {
      cells.push({ ...value, col: col + i, row: 0, ref: formatRef(0, col + i) });
    }
    col += repeat;
  }

  return { cells, hasFormulas };
}

type CellRead = Omit<XlsxCell, 'ref' | 'row' | 'col'>;

/** Read one cell, or null when it carries nothing worth materialising. */
function readCell(xml: string, span: ElementSpan): CellRead | null {
  const formulaRaw = attr(span.attrs, 'table:formula');
  const valueType = attr(span.attrs, 'office:value-type');
  const numberRaw = attr(span.attrs, 'office:value');

  const displayed = span.selfClosing ? '' : readCellText(xml, span);
  const numeric = numberRaw !== null && numberRaw !== '' && Number.isFinite(Number(numberRaw))
    ? Number(numberRaw)
    : null;

  // A cell with only a style — the common case in a padded sheet — is not
  // content. Everything else is, including a formula whose result is blank.
  if (displayed === '' && numeric === null && formulaRaw === null) {
    const typed = valueType !== null && valueType !== 'void';
    if (!typed) return null;
  }

  return {
    text: displayed !== '' ? displayed : formatTypedValue(span, valueType, numeric),
    numeric,
    formula: formulaRaw === null ? null : odfFormulaToDisplay(formulaRaw),
  };
}

/**
 * The value ODF stores separately from the displayed text.
 *
 * `office:value-type` decides which attribute holds it. This is only consulted
 * when the cell has no `text:p` to display — normally a writer emits both, and
 * the displayed form is the one it formatted, which is what a reader should
 * show (§15 takes the same position: show the last saved result, do not
 * recompute).
 */
function formatTypedValue(
  span: ElementSpan, valueType: string | null, numeric: number | null,
): string {
  switch (valueType) {
    case 'date': return attr(span.attrs, 'office:date-value') ?? '';
    case 'time': return attr(span.attrs, 'office:time-value') ?? '';
    case 'boolean': return attr(span.attrs, 'office:boolean-value') === 'true' ? 'TRUE' : 'FALSE';
    case 'string': return attr(span.attrs, 'office:string-value') ?? '';
    default: return numeric === null ? '' : String(numeric);
  }
}

/** Displayed text of a cell: its paragraphs, one per line. */
function readCellText(xml: string, span: ElementSpan): string {
  const lines: string[] = [];
  for (const paragraph of childElements(xml, span, ['text:p', 'text:h'])) {
    let text = '';
    /** Non-zero while inside a comment, whose text is not the cell's value. */
    let inAnnotation = 0;
    for (const token of tokenize(xml, paragraph.innerStart, paragraph.innerEnd)) {
      if (token.kind === 'text') {
        if (inAnnotation === 0) text += token.text;
        continue;
      }
      if (token.kind === 'empty') {
        if (inAnnotation > 0) continue;
        if (token.name === 'text:s') text += ' '.repeat(Math.max(1, intAttr(token.attrs, 'text:c', 1)));
        else if (token.name === 'text:tab') text += '\t';
        continue;
      }
      if (token.kind === 'open' && token.name === 'office:annotation') inAnnotation += 1;
      else if (token.kind === 'close' && token.name === 'office:annotation') inAnnotation -= 1;
    }
    lines.push(text);
  }
  return lines.join('\n');
}

/** Rows, descending through the header/group wrappers ODF permits. */
function* tableRows(xml: string, span: ElementSpan): Generator<ElementSpan> {
  const groups = [
    'table:table-row', 'table:table-header-rows', 'table:table-rows',
    'table:table-row-group',
  ];
  for (const child of childElements(xml, span, groups)) {
    if (child.name === 'table:table-row') yield child;
    else yield* tableRows(xml, child);
  }
}

function findSpreadsheetBody(xml: string): ElementSpan | null {
  // `office:body` is nested inside `office:document-content`, so it is found by
  // depth-aware search rather than as a top-level child.
  const body = findElement(xml, 'office:body');
  if (!body) return null;
  return childElements(xml, body, ['office:spreadsheet'])[0] ?? null;
}

/* ------------------------------------------------------------------ *
 * formulas
 * ------------------------------------------------------------------ */

/**
 * Turn an ODF formula into the form a person recognises.
 *
 * OpenFormula writes `of:=[.B2]-[.B3]` where a spreadsheet shows `=B2-B3`: a
 * namespace prefix, square brackets around every reference, a leading dot for
 * "this sheet", and semicolons between arguments. §15 says the formula bar
 * shows the formula that produced the cached result; showing the wire form
 * would satisfy the letter of that and none of the point.
 *
 * The conversion is display-only. Nothing writes a formula back — typing over
 * a formula cell replaces it with a literal, which the formula bar says before
 * the edit lands.
 */
export function odfFormulaToDisplay(raw: string): string {
  let text = raw.replace(/^[a-z][\w-]*:/i, '');
  text = text.replace(/\[([^\]]*)\]/g, (_whole, inner: string) => inner
    .split(':')
    .map((part) => part.replace(/^\$?\./, '').replace(/^\$/, ''))
    .join(':'));
  text = text.replace(/;/g, ',');
  return text.startsWith('=') ? text : `=${text}`;
}

/* ------------------------------------------------------------------ *
 * editing
 * ------------------------------------------------------------------ */

export interface OdfCellEdit {
  readonly ref: string;
  readonly value: CellValue;
}

export interface OdfSheetEditResult {
  readonly xml: string;
  /** Refs whose formula was removed because a literal value replaced it. */
  readonly formulasReplaced: readonly string[];
  readonly failed: readonly string[];
}

/**
 * Apply cell edits to one sheet inside `content.xml`.
 *
 * Each edit is applied to the XML the previous one produced, and the target is
 * located again each time. Splitting a repeat group changes every offset after
 * it, so a plan computed once against the original text would be wrong by the
 * second edit — and wrong in the silent way, writing a value into a neighbour.
 * Re-locating costs a scan per edit, which is nothing against the number of
 * cells a person edits by hand.
 */
export function applyOdfCellEdits(
  xml: string, sheetIndex: number, edits: readonly OdfCellEdit[],
): OdfSheetEditResult {
  const formulasReplaced: string[] = [];
  const failed: string[] = [];
  let out = xml;

  for (const edit of edits) {
    const parsed = parseRef(edit.ref);
    if (!parsed) { failed.push(edit.ref); continue; }
    try {
      const result = writeCell(out, sheetIndex, parsed.row, parsed.col, edit.value);
      out = result.xml;
      if (result.hadFormula) formulasReplaced.push(parsed.ref);
    } catch {
      failed.push(parsed.ref);
    }
  }

  return { xml: out, formulasReplaced, failed };
}

interface WriteResult {
  readonly xml: string;
  readonly hadFormula: boolean;
}

function writeCell(
  xml: string, sheetIndex: number, row: number, col: number, value: CellValue,
): WriteResult {
  const body = findSpreadsheetBody(xml);
  if (!body) throw new HandlerError('error.unableToRead', 'no office:spreadsheet body');
  const table = childElements(xml, body, ['table:table'])[sheetIndex];
  if (!table) throw new HandlerError('error.unableToRead', `no sheet at index ${sheetIndex}`);

  // 1. The row. Splitting a repeat group happens first, because it changes the
  //    offsets the cell search will run against.
  const located = locateRow(xml, table, row);
  if (!located) return { xml: appendRow(xml, table, row, col, value), hadFormula: false };

  if (located.repeat > 1) {
    const split = splitRepeat(
      xml, located.span, 'table:number-rows-repeated',
      located.offset, located.repeat,
    );
    // Re-run against the split text: the target row is now its own element.
    return writeCell(split, sheetIndex, row, col, value);
  }

  // 2. The cell within that row.
  const cell = locateCell(xml, located.span, col);
  if (!cell) {
    return { xml: appendCell(xml, located.span, col, value), hadFormula: false };
  }
  if (cell.repeat > 1) {
    const split = splitRepeat(
      xml, cell.span, 'table:number-columns-repeated', cell.offset, cell.repeat,
    );
    return writeCell(split, sheetIndex, row, col, value);
  }

  const hadFormula = attr(cell.span.attrs, 'table:formula') !== null;
  const styleName = attr(cell.span.attrs, 'table:style-name');
  const replacement = buildOdfCell(value, styleName, cellSpanAttrs(cell.span.attrs));

  return {
    xml: xml.slice(0, cell.span.start) + replacement + xml.slice(cell.span.end),
    hadFormula,
  };
}

/** Merge spans are geometry, not content: an edit must not undo one. */
function cellSpanAttrs(attrs: string): string {
  const parts: string[] = [];
  for (const name of ['table:number-columns-spanned', 'table:number-rows-spanned']) {
    const value = attr(attrs, name);
    if (value !== null && value !== '1') parts.push(` ${name}="${escapeXml(value)}"`);
  }
  return parts.join('');
}

interface Located {
  readonly span: ElementSpan;
  /** Index of the target *within* the repeat group. */
  readonly offset: number;
  readonly repeat: number;
}

function locateRow(xml: string, table: ElementSpan, row: number): Located | null {
  let cursor = 0;
  for (const span of tableRows(xml, table)) {
    const repeat = intAttr(span.attrs, 'table:number-rows-repeated', 1);
    if (row < cursor + repeat) return { span, offset: row - cursor, repeat };
    cursor += repeat;
  }
  return null;
}

function locateCell(xml: string, rowSpan: ElementSpan, col: number): Located | null {
  let cursor = 0;
  for (const span of childElements(xml, rowSpan, ['table:table-cell', 'table:covered-table-cell'])) {
    const repeat = intAttr(span.attrs, 'table:number-columns-repeated', 1);
    if (col < cursor + repeat) return { span, offset: col - cursor, repeat };
    cursor += repeat;
  }
  return null;
}

/**
 * Replace one repeated element with up to three, so the target stands alone.
 *
 * This is the operation that makes ODS editing correct rather than
 * approximately correct. `<table:table-cell table:number-columns-repeated="10"/>`
 * covering columns C–L becomes, for an edit at F, a repeat of 3, a single cell,
 * and a repeat of 6 — after which the caller rewrites the single one.
 */
function splitRepeat(
  xml: string, span: ElementSpan, attribute: string, offset: number, repeat: number,
): string {
  const source = xml.slice(span.start, span.end);
  const before = offset;
  const after = repeat - offset - 1;

  const withRepeat = (count: number): string => {
    if (count <= 0) return '';
    const stripped = source.replace(
      new RegExp(`\\s*${attribute}\\s*=\\s*"[^"]*"`), '',
    );
    if (count === 1) return stripped;
    // Quote-aware, because an attribute value may legally contain `>`.
    const insertAt = openTagEnd(stripped);
    const at = stripped[insertAt - 1] === '/' ? insertAt - 1 : insertAt;
    return `${stripped.slice(0, at)} ${attribute}="${count}"${stripped.slice(at)}`;
  };

  return xml.slice(0, span.start)
    + withRepeat(before) + withRepeat(1) + withRepeat(after)
    + xml.slice(span.end);
}

/** Index of the `>` ending the opening tag of `source`, quote-aware. */
function openTagEnd(source: string): number {
  let quote: string | null = null;
  for (let i = 0; i < source.length; i++) {
    const ch = source[i]!;
    if (quote) { if (ch === quote) quote = null; continue; }
    if (ch === '"' || ch === "'") quote = ch;
    else if (ch === '>') return i;
  }
  return source.length;
}

/** Extend a table downwards to reach a row it does not have. */
function appendRow(
  xml: string, table: ElementSpan, row: number, col: number, value: CellValue,
): string {
  let used = 0;
  for (const span of tableRows(xml, table)) {
    used += intAttr(span.attrs, 'table:number-rows-repeated', 1);
  }
  const gap = row - used;
  if (gap < 0) throw new HandlerError('error.unableToRead', 'row bookkeeping disagreed');

  const filler = gap === 0 ? ''
    : gap === 1 ? '<table:table-row><table:table-cell/></table:table-row>'
      : `<table:table-row table:number-rows-repeated="${gap}"><table:table-cell/></table:table-row>`;
  const lead = col === 0 ? ''
    : col === 1 ? '<table:table-cell/>'
      : `<table:table-cell table:number-columns-repeated="${col}"/>`;

  const insertion = `${filler}<table:table-row>${lead}${buildOdfCell(value, null, '')}</table:table-row>`;
  return xml.slice(0, table.innerEnd) + insertion + xml.slice(table.innerEnd);
}

/** Extend a row rightwards to reach a column it does not have. */
function appendCell(
  xml: string, rowSpan: ElementSpan, col: number, value: CellValue,
): string {
  let used = 0;
  for (const span of childElements(xml, rowSpan, ['table:table-cell', 'table:covered-table-cell'])) {
    used += intAttr(span.attrs, 'table:number-columns-repeated', 1);
  }
  const gap = col - used;
  if (gap < 0) throw new HandlerError('error.unableToRead', 'column bookkeeping disagreed');

  const lead = gap === 0 ? ''
    : gap === 1 ? '<table:table-cell/>'
      : `<table:table-cell table:number-columns-repeated="${gap}"/>`;

  const insertion = `${lead}${buildOdfCell(value, null, '')}`;
  return xml.slice(0, rowSpan.innerEnd) + insertion + xml.slice(rowSpan.innerEnd);
}

/**
 * Build a `table:table-cell` element for a value.
 *
 * Both the typed value and the displayed text are written. ODF readers use the
 * typed attribute for calculation and the `text:p` for display, and a cell that
 * carried only one of them would look right in one application and empty in
 * another.
 *
 * The original cell's `table:style-name` is carried over, so a number that was
 * formatted as currency stays formatted as currency. `table:formula` is not
 * carried over, and that is the §15 promise being kept: this application cannot
 * calculate, so it never writes a formula whose result it could not produce.
 */
export function buildOdfCell(
  value: CellValue, styleName: string | null, extraAttrs: string,
): string {
  const style = styleName ? ` table:style-name="${escapeXml(styleName)}"` : '';
  const tail = `${style}${extraAttrs}`;

  switch (value.kind) {
    case 'number':
      return `<table:table-cell${tail} office:value-type="float" office:value="${value.value}">`
        + `<text:p>${escapeXml(String(value.value))}</text:p></table:table-cell>`;
    case 'boolean':
      return `<table:table-cell${tail} office:value-type="boolean" office:boolean-value="${value.value}">`
        + `<text:p>${value.value ? 'TRUE' : 'FALSE'}</text:p></table:table-cell>`;
    case 'text':
      return `<table:table-cell${tail} office:value-type="string">`
        + `<text:p>${escapeXml(value.text)}</text:p></table:table-cell>`;
    case 'empty':
    default:
      return `<table:table-cell${tail}/>`;
  }
}
